/**
 * Pure view helpers over the billing-status contract (`@beheard/billing-contract`).
 *
 * RULES (RC7):
 *  - Unknown is "unavailable", never 0: a missing field, a failed request or a
 *    pool the server could not read renders as unavailable.
 *  - Display only. The server is authoritative for admission (402/429/503);
 *    nothing here may disable an action. The single exception a screen may
 *    show is "used up", and only when a pool is KNOWN to be exhausted.
 *  - The safety pools are a LOCAL NONCOMMERCIAL SAFETY LIMIT: worded as a
 *    monthly allowance, never as credits, never as something to buy.
 */
import type { AllowancePoolKey, BillingStatus, SafetyAllowanceStatus } from '@beheard/billing-contract';
import { BILLING_PLAN_NAMES } from '@beheard/billing-contract';
import { getUsageAllowanceRule } from '@beheard/limits';

export type AllowanceView =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  /**
   * The pool is not metered per use on this plan (the contract's
   * `state: 'unlimited'`). NOT "unlimited": session limits and the every-plan
   * daily safety limits still apply (RC19) — see notMeteredText.
   */
  | { kind: 'not_metered'; pool: AllowancePoolKey }
  | { kind: 'limited'; remaining: number; monthly: number };

export function allowanceView(
  status: BillingStatus | undefined,
  pool: AllowancePoolKey,
  q: { isLoading?: boolean; isError?: boolean } = {}
): AllowanceView {
  if (q.isLoading && !status) return { kind: 'loading' };
  const p: SafetyAllowanceStatus | undefined = status?.safetyAllowance?.pools?.[pool];
  if (q.isError || !p) return { kind: 'unavailable' };
  if (p.state === 'unlimited') return { kind: 'not_metered', pool };
  if (p.state === 'limited' && typeof p.remaining === 'number' && typeof p.monthly === 'number') {
    return { kind: 'limited', remaining: p.remaining, monthly: p.monthly };
  }
  return { kind: 'unavailable' };
}

/** True only when the server reported the pool as used up. Unknown is NOT exhausted. */
export function isKnownExhausted(view: AllowanceView): boolean {
  return view.kind === 'limited' && view.remaining <= 0;
}

/**
 * The every-plan daily SAFETY limit that still bounds a pool that is not
 * metered per use, when one is known (types/usage.ts safetyLimitPerDay —
 * the practice pool's voice analyses). Null when none applies.
 */
export function poolSafetyLimitText(pool: AllowancePoolKey): string | null {
  if (pool !== 'practice') return null;
  const max = getUsageAllowanceRule('voice_practice_classic')?.safetyLimitPerDay;
  return typeof max === 'number' ? `safety limit: ${max} voice analyses a day` : null;
}

/** "Not metered per use (safety limit: <the rule's number> voice analyses a day)" — never "Unlimited". */
export function notMeteredText(pool: AllowancePoolKey): string {
  const safety = poolSafetyLimitText(pool);
  return safety ? `Not metered per use (${safety})` : 'Not metered per use';
}

/** "Not metered per use (…)" | "87 of 100 left this month" | "Unavailable" | null while loading. */
export function allowanceText(view: AllowanceView): string | null {
  switch (view.kind) {
    case 'loading':
      return null;
    case 'not_metered':
      return notMeteredText(view.pool);
    case 'limited':
      return `${view.remaining} of ${view.monthly} left this month`;
    default:
      return 'Unavailable';
  }
}

/** A Central credit pool: a number when known, otherwise "Unavailable" (never 0). */
export function creditBalanceText(status: BillingStatus | undefined, pool: AllowancePoolKey): string {
  const c = status?.credits?.[pool];
  return c && typeof c.balance === 'number' ? String(c.balance) : 'Unavailable';
}

/** The capacity-tier name, or null when unknown (never a default "Free"). */
export function planNameOf(status: BillingStatus | undefined): string | null {
  const tier = status?.plan?.tier;
  return tier ? status?.plan?.name || BILLING_PLAN_NAMES[tier] : null;
}

/** The server's refusal message from an RTK Query error, if it sent one. */
export function serverMessageOf(error: unknown): string | null {
  const data = (error as { data?: { message?: unknown; error?: { message?: unknown } } } | undefined)?.data;
  if (typeof data?.message === 'string' && data.message) return data.message;
  if (typeof data?.error?.message === 'string' && data.error.message) return data.error.message;
  return null;
}
